import { useEffect, useRef } from 'react'
import { api, type VerificationDetail, type VerificationProgress } from './api.ts'
import { isAnalysing } from './vocabulary.ts'

/**
 * A loaded detail with the checkpoint moved on, and nothing else touched.
 *
 * Here rather than in each screen because both do exactly this, and because
 * `step` is optional-and-not-undefined: spreading a null through it is a type
 * error, and getting that wrong in two places is how they drift apart.
 */
export function withProgress(
  detail: VerificationDetail,
  progress: VerificationProgress,
): VerificationDetail {
  return {
    ...detail,
    /*
     * The workflow's view, moved on with the row's.
     *
     * The screens read `live.step ?? verification.step`, because while a run is
     * in flight the workflow used to be the fresher of the two. `live` only
     * ever arrives with the full detail call -- it is a Temporal query -- and
     * that call now happens once, when the analysis settles. Leaving it here
     * untouched froze the progress strip on whatever the workflow had said at
     * page load, which is the exact staleness the poll was added to remove.
     *
     * The row is not a worse answer: `persistStep` writes each checkpoint as
     * it is reached. So the poll's answer replaces both, and the precedence
     * above stops mattering rather than having to be unpicked.
     */
    ...(detail.live === undefined
      ? {}
      : {
          live: {
            ...detail.live,
            status: progress.status,
            ...(progress.step === null ? {} : { step: progress.step }),
          },
        }),
    verification: {
      ...detail.verification,
      status: progress.status,
      ...(progress.step === null ? {} : { step: progress.step }),
      failure_reason: progress.failureReason,
      // Left alone when the row carries none, rather than overwritten with an
      // empty one: the strip reads durations off it, and a blank timeline
      // renders as an analysis that has taken no time at all.
      ...(Array.isArray(progress.timeline) && progress.timeline.length > 0
        ? {
            timeline: progress.timeline as NonNullable<
              VerificationDetail['verification']['timeline']
            >,
          }
        : {}),
      stage_pos: progress.stagePos,
    },
  }
}

/**
 * Keeps an verification's checkpoint fresh while it is being analysed.
 *
 * ## Why this is not the detail call on a timer
 *
 * It was. `GET /:id` every three seconds, per open tab: the prescription, the
 * quotation, every finding, every decision, every block, two catalogue reads
 * and a Temporal query -- to learn which of seven words to underline. The
 * screen that most needed it, `VerificationDetail`, did not poll at all, so a
 * conferente watched "buscando orçamento" until they pressed Atualizar by
 * hand.
 *
 * `onProgress` gets the cheap answer as it changes. `onSettled` fires once,
 * when the analysis stops, and is where the caller loads everything properly
 * -- the poll that sees the status flip may run before the findings are
 * written, so the full read has to come after, not with it.
 *
 * ## Why the interval is not shorter
 *
 * Two seconds against a checkpoint that moves roughly seven times over
 * minutes. Faster would buy nothing a person can perceive and cost a request
 * per tab per second against the database a pharmacy's counter is using.
 */
const EVERY_MS = 2000

export function useVerificationProgress(
  id: string,
  active: boolean,
  onProgress: (progress: VerificationProgress) => void,
  onSettled: () => void,
): void {
  /*
   * Held in refs rather than listed as dependencies.
   *
   * Both are written fresh on every render by every caller that closes over
   * status, so depending on them would tear the timer down and build it up
   * again several times a second -- which is a poll that never fires.
   */
  const progressed = useRef(onProgress)
  const settled = useRef(onSettled)
  progressed.current = onProgress
  settled.current = onSettled

  const wasActive = useRef(active)

  useEffect(() => {
    if (!active) {
      // Once more after it ends, and only then: the caller wants the findings
      // the analysis just wrote, and nothing here has them.
      if (wasActive.current) settled.current()
      wasActive.current = false
      return undefined
    }
    wasActive.current = true

    let stopped = false
    const beat = async () => {
      try {
        const progress = await api.progress(id)
        if (stopped) return
        progressed.current(progress)
        /*
         * The end, noticed here rather than waited for.
         *
         * `active` is the caller's view of the status and only changes once
         * this beat has reported the new one, so waiting for the effect to
         * re-run would leave a finished verification showing its last
         * checkpoint until the next tick.
         */
        if (!isAnalysing(progress.status)) settled.current()
      } catch {
        // A failed beat is the next beat's problem. Reporting it would put an
        // error over a comparison that is still perfectly readable.
      }
    }

    const timer = setInterval(() => void beat(), EVERY_MS)
    return () => {
      stopped = true
      clearInterval(timer)
    }
  }, [active, id])
}
